import { Alert, AlertDescription } from '@/components/ui/alert'
import { Info } from 'lucide-react'
import { usePermissions } from '@/lib/hooks'
import { useProject } from '@/contexts/project-context'
import { useUser } from '@/lib/hooks'

interface RoleNotificationProps {
  feature: 'assignTasks' | 'editProject' | 'inviteUsers' | 'manageMembers' | 'editTasks'
  customMessage?: string
}

export function RoleNotification({ feature, customMessage }: RoleNotificationProps) {
  const permissions = usePermissions()
  const { currentProject, projectMembers } = useProject()
  const { profile: user } = useUser()
  
  // Get user's role in current project
  const userProjectMember = projectMembers.find(
    m => m.project_id === currentProject?.id && m.user_id === user?.id
  )
  const projectRole = userProjectMember?.role
  
  // Check if user has permission for the feature
  const hasPermission = permissions[`can${feature.charAt(0).toUpperCase() + feature.slice(1)}` as keyof typeof permissions]
  
  if (hasPermission) return null
  
  // Generate appropriate message based on feature
  const getMessage = () => {
    if (customMessage) return customMessage
    
    switch (feature) {
      case 'assignTasks':
        return `Only project admins and owners can assign team members to tasks. You are currently a ${projectRole || 'member'} in this project.`
      case 'editProject':
        return `Only project admins and owners can edit project details. You are currently a ${projectRole || 'member'} in this project.`
      case 'editTasks':
        return `Only project admins and owners can edit task details. You are currently a ${projectRole || 'member'} in this project.`
      case 'inviteUsers':
        return 'Only system admins and founders can invite new users to the platform.'
      case 'manageMembers':
        return `Only project admins and owners can manage project members. You are currently a ${projectRole || 'member'} in this project.`
      default:
        return 'You do not have permission to perform this action.'
    }
  }
  
  return (
    <Alert className="mb-4">
      <Info className="h-4 w-4" />
      <AlertDescription>{getMessage()}</AlertDescription>
    </Alert>
  )
}